import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AssignmentService from "services/assignmentService";

export const getFormationAssignments = createAsyncThunk(
  "assignments/getFormationAssignments",
  async (formationId, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.getFormationAssignments(formationId);
      return response.data?.data || response.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const getFormationStructure = createAsyncThunk(
  "assignments/getFormationStructure",
  async (formationId, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.getFormationStructure(formationId);
      return response.data?.data || response.data || {};
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const getCourseAssignments = createAsyncThunk(
  "assignments/getCourseAssignments",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.getCourseAssignments(courseId);
      return response.data?.data || response.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const getCourseStructure = createAsyncThunk(
  "assignments/getCourseStructure",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.getCourseStructure(courseId);
      return response.data?.data || response.data || {};
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const createAssignment = createAsyncThunk(
  "assignments/createAssignment",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.createAssignment(formData);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const updateAssignment = createAsyncThunk(
  "assignments/updateAssignment",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.updateAssignment(id, formData);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const deleteAssignment = createAsyncThunk(
  "assignments/deleteAssignment",
  async (id, { rejectWithValue }) => {
    try {
      await AssignmentService.deleteAssignment(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const toggleSolutionVisibility = createAsyncThunk(
  "assignments/toggleSolutionVisibility",
  async ({ id, solutionVisible }, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.toggleSolution(id, solutionVisible);
      return response.data?.data || { _id: id, solutionVisible };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const toggleAssignmentPublish = createAsyncThunk(
  "assignments/toggleAssignmentPublish",
  async ({ id, isPublished }, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.togglePublish(id, isPublished);
      return response.data?.data || { _id: id, isPublished };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const getAssignmentSubmissions = createAsyncThunk(
  "assignments/getAssignmentSubmissions",
  async (assignmentId, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.getAssignmentSubmissions(assignmentId);
      return response.data?.data || response.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

export const reviewSubmission = createAsyncThunk(
  "assignments/reviewSubmission",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await AssignmentService.reviewSubmission(id, formData);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Internal server error");
    }
  }
);

const initialState = {
  assignments: [],
  structure: null,
  submissions: [],
  loading: false,
  structureLoading: false,
  submissionsLoading: false,
  actionLoading: false,
  error: null,
};

const assignmentsSlice = createSlice({
  name: "assignments",
  initialState,
  reducers: {
    clearSubmissions: (state) => {
      state.submissions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // getFormationAssignments
      .addCase(getFormationAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFormationAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload || [];
      })
      .addCase(getFormationAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // getFormationStructure
      .addCase(getFormationStructure.pending, (state) => {
        state.structureLoading = true;
      })
      .addCase(getFormationStructure.fulfilled, (state, action) => {
        state.structureLoading = false;
        state.structure = action.payload;
      })
      .addCase(getFormationStructure.rejected, (state) => {
        state.structureLoading = false;
      })

      // getCourseAssignments
      .addCase(getCourseAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCourseAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload || [];
      })
      .addCase(getCourseAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // getCourseStructure
      .addCase(getCourseStructure.pending, (state) => {
        state.structureLoading = true;
      })
      .addCase(getCourseStructure.fulfilled, (state, action) => {
        state.structureLoading = false;
        state.structure = action.payload;
      })
      .addCase(getCourseStructure.rejected, (state) => {
        state.structureLoading = false;
      })

      // createAssignment
      .addCase(createAssignment.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createAssignment.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload) {
          state.assignments.push(action.payload);
        }
      })
      .addCase(createAssignment.rejected, (state) => {
        state.actionLoading = false;
      })

      // updateAssignment
      .addCase(updateAssignment.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateAssignment.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload) {
          const index = state.assignments.findIndex((a) => a._id === action.payload._id);
          if (index !== -1) {
            state.assignments[index] = {
              ...state.assignments[index],
              ...action.payload,
            };
          }
        }
      })
      .addCase(updateAssignment.rejected, (state) => {
        state.actionLoading = false;
      })

      // deleteAssignment
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        state.assignments = state.assignments.filter((a) => a._id !== action.payload);
      })

      // toggleSolutionVisibility
      .addCase(toggleSolutionVisibility.fulfilled, (state, action) => {
        const item = state.assignments.find((a) => a._id === action.payload._id);
        if (item) {
          item.solutionVisible = action.payload.solutionVisible;
        }
      })

      // toggleAssignmentPublish
      .addCase(toggleAssignmentPublish.fulfilled, (state, action) => {
        const item = state.assignments.find((a) => a._id === action.payload._id);
        if (item) {
          item.isPublished = action.payload.isPublished;
        }
      })

      // getAssignmentSubmissions
      .addCase(getAssignmentSubmissions.pending, (state) => {
        state.submissionsLoading = true;
      })
      .addCase(getAssignmentSubmissions.fulfilled, (state, action) => {
        state.submissionsLoading = false;
        state.submissions = action.payload || [];
      })
      .addCase(getAssignmentSubmissions.rejected, (state) => {
        state.submissionsLoading = false;
      })

      // reviewSubmission
      .addCase(reviewSubmission.fulfilled, (state, action) => {
        if (action.payload) {
          const index = state.submissions.findIndex((s) => s._id === action.payload._id);
          if (index !== -1) {
            state.submissions[index] = action.payload;
          }
        }
      });
  },
});

export const { clearSubmissions } = assignmentsSlice.actions;
export default assignmentsSlice.reducer;
